import React, { useState } from 'react';
import { Calendar, FileText, TrendingUp, DollarSign, Clock, Briefcase, ArrowUp, ArrowDown, MoreVertical, Eye, Download, RefreshCw } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import HeaderTopSection from "../../components/partials/HeaderTop";
import '../../assets/css/DashboardPage.css';
import Banner from "../../components/partials/Banner";

const ProfessionalDashboard = () => {
    const [timeRange, setTimeRange] = useState('7d');
    const [isLoading, setIsLoading] = useState(false);

    // Données simulées pour un professionnel
    const stats = [
        {
            title: 'Rendez-vous',
            value: '24',
            change: '+18%',
            trend: 'up',
            icon: Calendar,
            color: '#3b82f6',
            bgColor: '#eff6ff'
        },
        {
            title: 'Clients Actifs',
            value: '15',
            change: '+3',
            trend: 'up',
            icon: Briefcase,
            color: '#10b981',
            bgColor: '#f0fdf4'
        },
        {
            title: 'Revenus',
            value: '€3,245',
            change: '+22.5%',
            trend: 'up',
            icon: DollarSign,
            color: '#8b5cf6',
            bgColor: '#faf5ff'
        },
        {
            title: 'Taux d\'occupation',
            value: '68%',
            change: '+5.2%',
            trend: 'up',
            icon: TrendingUp,
            color: '#f59e0b',
            bgColor: '#fffbeb'
        }
    ];

    const chartData = [
        { name: 'Lun', appointments: 2, revenue: 420, duration: 3.5 },
        { name: 'Mar', appointments: 5, revenue: 780, duration: 8.2 },
        { name: 'Mer', appointments: 3, revenue: 540, duration: 5.0 },
        { name: 'Jeu', appointments: 6, revenue: 920, duration: 9.1 },
        { name: 'Ven', appointments: 4, revenue: 680, duration: 6.8 },
        { name: 'Sam', appointments: 3, revenue: 540, duration: 5.5 },
        { name: 'Dim', appointments: 1, revenue: 180, duration: 1.5 }
    ];

    const serviceData = [
        { name: 'Consultation', value: 45, color: '#3B82F6' },
        { name: 'Suivi', value: 30, color: '#10B981' },
        { name: 'Urgence', value: 15, color: '#F59E0B' },
        { name: 'Téléconsultation', value: 10, color: '#8B5CF6' }
    ];

    const recentAppointments = [
        { id: 1, client: 'Marie Dubois', service: 'Consultation', time: 'Aujourd\'hui, 10h-10h30', status: 'confirmé' },
        { id: 2, client: 'Jean Martin', service: 'Suivi', time: 'Aujourd\'hui, 14h-14h45', status: 'confirmé' },
        { id: 3, client: 'Sophie Chen', service: 'Urgence', time: 'Demain, 9h-9h30', status: 'à confirmer' },
        { id: 4, client: 'Lucas Petit', service: 'Téléconsultation', time: 'Demain, 16h-16h30', status: 'confirmé' },
        { id: 5, client: 'Emma Moreau', service: 'Consultation', time: 'Après-demain, 11h-11h45', status: 'à confirmer' }
    ];

    const handleRefresh = () => {
        setIsLoading(true);
        setTimeout(() => setIsLoading(false), 1000);
    };

    return (
        <>
            <HeaderTopSection />
            <Banner
                title="Tableu de Bord"
                backgroundImage="https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
            />
            <div className="dashboard-container">
                {/* Header Dashboard */}
                <div className="dashboard-header">
                    <div className="dashboard-header-content">
                        <div>
                            <h1 className="dashboard-header-title">Tableau de Bord Professionnel</h1>
                            <p className="dashboard-header-subtitle">Vue d'ensemble de votre activité</p>
                        </div>
                        <div className="dashboard-header-controls">
                            <select
                                value={timeRange}
                                onChange={(e) => setTimeRange(e.target.value)}
                                className="dashboard-select"
                            >
                                <option value="7d">7 derniers jours</option>
                                <option value="30d">30 derniers jours</option>
                                <option value="90d">3 derniers mois</option>
                            </select>
                            <button
                                onClick={handleRefresh}
                                disabled={isLoading}
                                className="dashboard-refresh-button"
                            >
                                <RefreshCw style={{
                                    width: '1rem',
                                    height: '1rem',
                                    animation: isLoading ? 'spin 1s linear infinite' : 'none'
                                }} />
                                <span>Actualiser</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="dashboard-content">
                    {/* Stats Cards */}
                    <div className="dashboard-stats-grid">
                        {stats.map((stat, index) => (
                            <div
                                key={index}
                                className="dashboard-stat-card"
                            >
                                <div className="dashboard-stat-card-header">
                                    <div className="dashboard-stat-icon" style={{ backgroundColor: stat.bgColor }}>
                                        <stat.icon style={{ width: '1.5rem', height: '1.5rem', color: stat.color }} />
                                    </div>
                                    <button style={{
                                        padding: '0.25rem',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        borderRadius: '0.25rem',
                                        cursor: 'pointer'
                                    }}>
                                        <MoreVertical style={{ width: '1rem', height: '1rem', color: '#9ca3af' }} />
                                    </button>
                                </div>
                                <div>
                                    <h3 className="dashboard-stat-title">{stat.title}</h3>
                                    <p className="dashboard-stat-value">{stat.value}</p>
                                    <div className={`dashboard-stat-change ${stat.trend === 'up' ? 'dashboard-stat-change-up' : 'dashboard-stat-change-down'}`}>
                                        {stat.trend === 'up' ?
                                            <ArrowUp style={{ width: '1rem', height: '1rem', marginRight: '0.25rem' }} /> :
                                            <ArrowDown style={{ width: '1rem', height: '1rem', marginRight: '0.25rem' }} />
                                        }
                                        <span>{stat.change}</span>
                                        <span className="dashboard-stat-change-text">vs période précédente</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Charts Row */}
                    <div className="dashboard-charts-grid">
                        {/* Line Chart */}
                        <div className="dashboard-chart-card">
                            <div className="dashboard-chart-header">
                                <div>
                                    <h3 className="dashboard-chart-title">Activité Hebdomadaire</h3>
                                    <p className="dashboard-chart-subtitle">Rendez-vous et revenus</p>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <button style={{
                                        padding: '0.5rem',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        borderRadius: '0.5rem',
                                        cursor: 'pointer'
                                    }}>
                                        <Eye style={{ width: '1rem', height: '1rem', color: '#6b7280' }} />
                                    </button>
                                    <button style={{
                                        padding: '0.5rem',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        borderRadius: '0.5rem',
                                        cursor: 'pointer'
                                    }}>
                                        <Download style={{ width: '1rem', height: '1rem', color: '#6b7280' }} />
                                    </button>
                                </div>
                            </div>
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={chartData}>
                                    <defs>
                                        <linearGradient id="colorAppointments" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                                        </linearGradient>
                                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="name" stroke="#9CA3AF" />
                                    <YAxis yAxisId="left" stroke="#3B82F6" />
                                    <YAxis yAxisId="right" orientation="right" stroke="#10B981" />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: 'white',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                        }}
                                    />
                                    <Area
                                        yAxisId="left"
                                        type="monotone"
                                        dataKey="appointments"
                                        stroke="#3B82F6"
                                        strokeWidth={2}
                                        fillOpacity={1}
                                        fill="url(#colorAppointments)"
                                        name="Rendez-vous"
                                    />
                                    <Area
                                        yAxisId="right"
                                        type="monotone"
                                        dataKey="revenue"
                                        stroke="#10B981"
                                        strokeWidth={2}
                                        fillOpacity={1}
                                        fill="url(#colorRevenue)"
                                        name="Revenus (€)"
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Pie Chart */}
                        <div className="dashboard-chart-card">
                            <div className="dashboard-chart-header">
                                <div>
                                    <h3 className="dashboard-chart-title">Répartition des Prestations</h3>
                                    <p className="dashboard-chart-subtitle">Par type de service</p>
                                </div>
                            </div>
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart>
                                    <Pie
                                        data={serviceData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={100}
                                        paddingAngle={2}
                                        dataKey="value"
                                    >
                                        {serviceData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="dashboard-pie-chart-legend">
                                {serviceData.map((item, index) => (
                                    <div key={index} className="dashboard-legend-item">
                                        <div className="dashboard-legend-label">
                                            <div className="dashboard-legend-color" style={{ backgroundColor: item.color }}></div>
                                            <span>{item.name}</span>
                                        </div>
                                        <span className="dashboard-legend-value">{item.value}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Bottom Row */}
                    <div className="dashboard-bottom-grid">
                        {/* Revenue Chart */}
                        <div className="dashboard-chart-card">
                            <div className="dashboard-chart-header">
                                <div>
                                    <h3 className="dashboard-chart-title">Temps de Travail</h3>
                                    <p className="dashboard-chart-subtitle">Heures passées avec les clients</p>
                                </div>
                            </div>
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="name" stroke="#9CA3AF" />
                                    <YAxis stroke="#9CA3AF" />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: 'white',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                        }}
                                    />
                                    <Bar dataKey="duration" fill="#F59E0B" radius={[4, 4, 0, 0]} name="Heures" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Prochains Rendez-vous */}
                        <div className="dashboard-chart-card">
                            <div className="dashboard-chart-header">
                                <div>
                                    <h3 className="dashboard-chart-title">Prochains Rendez-vous</h3>
                                    <p className="dashboard-chart-subtitle">Vos rendez-vous à venir</p>
                                </div>
                                <button style={{
                                    color: '#2563eb',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                }}>
                                    Voir tout
                                </button>
                            </div>
                            <div className="dashboard-activities-list">
                                {recentAppointments.map((appointment) => (
                                    <div
                                        key={appointment.id}
                                        className="dashboard-activity-item"
                                    >
                                        <div className="dashboard-activity-avatar">
                                            {appointment.client.split(' ').map(n => n[0]).join('')}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <p className="dashboard-activity-text">
                                                <span style={{ fontWeight: '500' }}>{appointment.client}</span> - {appointment.service}
                                            </p>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <p className="dashboard-activity-time">{appointment.time}</p>
                                                <span style={{
                                                    padding: '0.25rem 0.5rem',
                                                    borderRadius: '0.25rem',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '500',
                                                    backgroundColor: appointment.status === 'confirmé' ? '#D1FAE5' : '#FEF3C7',
                                                    color: appointment.status === 'confirmé' ? '#065F46' : '#92400E'
                                                }}>
                                                    {appointment.status}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ProfessionalDashboard;